import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Calendar,
  Heart,
  Menu,
  X,
  Monitor,
  Plus,
  User,
  LogOut,
  Package,
  Barcode
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

export const MobileLayout = ({ children, user, onLogout, onSwitchToFull }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isHome = location.pathname === "/" || location.pathname === "/mobile";

  return (
    <div className="min-h-screen bg-brand-cream">
      {/* Brand gradient stripe */}
      <div className="fixed top-0 left-0 right-0 h-1 brand-gradient z-[60]" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-stone-200 px-4 py-3 mt-1">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-lg font-semibold brand-purple">
            Circulate
          </h1>
          
          <div className="flex items-center gap-2">
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <SheetHeader>
                  <SheetTitle className="text-left">Menu</SheetTitle>
                </SheetHeader>
                <div className="mt-6 space-y-4">
                  {/* User Info */}
                  <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{user?.name}</p>
                      <Badge variant="outline" className="text-xs mt-0.5">
                        {user?.is_admin ? "Admin" : user?.role}
                      </Badge>
                    </div>
                  </div>

                  {/* Switch to Full View */}
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-3"
                    onClick={() => {
                      onSwitchToFull();
                      setMenuOpen(false);
                    }}
                    data-testid="switch-to-full-btn"
                  >
                    <Monitor className="h-4 w-4" />
                    Switch to Full View
                  </Button>

                  {/* Navigation */}
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wider px-2 mb-2">
                      Quick Access
                    </p>
                    <Button
                      variant={location.pathname === "/mobile/events" ? "secondary" : "ghost"}
                      className="w-full justify-start gap-3"
                      onClick={() => {
                        navigate("/mobile/events");
                        setMenuOpen(false);
                      }}
                    >
                      <Calendar className="h-4 w-4" />
                      Events
                    </Button>
                    <Button
                      variant={location.pathname === "/mobile/inventory" ? "secondary" : "ghost"}
                      className="w-full justify-start gap-3"
                      onClick={() => {
                        navigate("/mobile/inventory");
                        setMenuOpen(false);
                      }}
                    >
                      <Package className="h-4 w-4" />
                      Inventory
                    </Button>
                    <Button
                      variant={location.pathname === "/mobile/shout-outs" ? "secondary" : "ghost"}
                      className="w-full justify-start gap-3"
                      onClick={() => {
                        navigate("/mobile/shout-outs");
                        setMenuOpen(false);
                      }}
                    >
                      <Heart className="h-4 w-4" />
                      Shout Outs
                    </Button>
                    <Button
                      variant={location.pathname === "/mobile/offline-checkout" ? "secondary" : "ghost"}
                      className="w-full justify-start gap-3"
                      onClick={() => {
                        navigate("/mobile/offline-checkout");
                        setMenuOpen(false);
                      }}
                    >
                      <Barcode className="h-4 w-4" />
                      Offline Checkout
                    </Button>
                  </div>

                  {/* Logout */}
                  <div className="pt-4 border-t">
                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-3 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => {
                        onLogout();
                        setMenuOpen(false);
                      }}
                      data-testid="mobile-logout-btn"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign Out
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pb-20">
        {children}
      </main>

      {/* Bottom Navigation - only show when not on home */}
      {!isHome && (
        <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-stone-200 px-1 py-2 z-40">
          <div className="flex items-center justify-around">
            <Button
              variant="ghost"
              className={`flex-1 flex-col gap-1 h-auto py-2 px-1 ${
                location.pathname === "/mobile" ? "brand-purple" : "text-muted-foreground"
              }`}
              onClick={() => navigate("/mobile")}
            >
              <Plus className="h-5 w-5" />
              <span className="text-[10px]">Home</span>
            </Button>
            <Button
              variant="ghost"
              className={`flex-1 flex-col gap-1 h-auto py-2 px-1 ${
                location.pathname === "/mobile/events" ? "brand-blue" : "text-muted-foreground"
              }`}
              onClick={() => navigate("/mobile/events")}
            >
              <Calendar className="h-5 w-5" />
              <span className="text-[10px]">Events</span>
            </Button>
            <Button
              variant="ghost"
              className={`flex-1 flex-col gap-1 h-auto py-2 px-1 ${
                location.pathname === "/mobile/inventory" ? "brand-green" : "text-muted-foreground"
              }`}
              onClick={() => navigate("/mobile/inventory")}
            >
              <Package className="h-5 w-5" />
              <span className="text-[10px]">Inventory</span>
            </Button>
            <Button
              variant="ghost"
              className={`flex-1 flex-col gap-1 h-auto py-2 px-1 ${
                location.pathname === "/mobile/shout-outs" ? "brand-red" : "text-muted-foreground"
              }`}
              onClick={() => navigate("/mobile/shout-outs")}
            >
              <Heart className="h-5 w-5" />
              <span className="text-[10px]">Thanks</span>
            </Button>
          </div>
        </nav>
      )}
    </div>
  );
};
